import React from 'react';
import { useTranslation } from 'react-i18next';
import Spin from 'antd/es/spin';
import { ShieldX } from 'lucide-react';
import { usePermission } from '@hooks/usePermission';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission: string | readonly string[]; // Quyền cần có (PERM.*), danh sách = có ít nhất một quyền
  fallback?: React.ReactNode; // Nội dung thay thế khi không có quyền (mặc định: thông báo)
}

/**
 * Chặn chức năng theo ma trận phân quyền. Cách dùng giống RoleGuard (bọc route hoặc một khối):
 *   <PermissionGuard permission={PERM.student.practice}><StudentPracticeOverview /></PermissionGuard>
 * Không redirect: hiện thông báo không có quyền ngay tại chỗ.
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({ children, permission, fallback }) => {
  const { t } = useTranslation();
  const { can, loaded } = usePermission();

  if (!loaded) {
    return (
      <div className="flex items-center justify-center w-full min-h-[30vh]">
        <Spin size="large" />
      </div>
    );
  }

  if (!can(permission)) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div className="flex flex-col items-center justify-center w-full min-h-[30vh] text-slate-600">
        <ShieldX size={56} className="text-primary mb-3" />
        <p className="text-center text-base px-4">{t('permission.no_access')}</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default PermissionGuard;
